/*
 * HealthSparkline — Iter 91.8 · restrained trend visualiser for a
 * partner org's `client.health_history[]`. Renders a monochrome 90-day
 * polyline showing the transition arc between the five health tiers
 * (strong/stable/watch/at_risk/critical) at a resolution of "one point
 * per history entry". No numbers, no independent scoring — this is a
 * trend surface, not a metric. Uses the same underlying data as the
 * Firm Brief health-shifts drill-through so the two surfaces stay in
 * lockstep on any future ranking change.
 *
 * Deliberately not exposed as a reusable chart primitive — the API is
 * intentionally narrow (history array in, mini SVG out) so a partner
 * scanning "clients at risk" gets one quiet visual signal per row.
 */
const RANK = { strong: 4, stable: 3, watch: 2, at_risk: 1, critical: 0 };
const WINDOW_DAYS = 90;

function ago(iso) {
  try { return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24); } catch { return null; }
}

export default function HealthSparkline({ history, width = 56, height = 14 }) {
  const arr = Array.isArray(history) ? history : [];
  // Filter to entries with a valid `current` tier within WINDOW_DAYS.
  const points = arr
    .map((e) => {
      if (!e || typeof e !== "object") return null;
      const tier = e.current || e.to || e.health;
      const iso = e.changed_at || e.at || e.date;
      const days = iso ? ago(iso) : null;
      if (!(tier in RANK) || days === null || days < 0 || days > WINDOW_DAYS) return null;
      return { rank: RANK[tier], days };
    })
    .filter(Boolean)
    .sort((a, b) => b.days - a.days); // oldest first
  if (points.length === 0) return null;

  // Trend-only: single-point renders as a subtle dot at its rank so
  // even one datum communicates "here's where they are" without
  // pretending to show movement.
  const xs = points.map((p) => WINDOW_DAYS - p.days);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const xSpan = Math.max(1, xMax - xMin);
  const rMax = 4; // RANK.strong
  const path = points
    .map((p, i) => {
      const x = ((xs[i] - xMin) / xSpan) * (width - 2) + 1;
      const y = height - 2 - (p.rank / rMax) * (height - 3);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  // Latest tier drives the stroke tone so the sparkline reads as a
  // trend AT the current health level, not a new independent signal.
  const latest = points[points.length - 1].rank;
  const tone = latest <= RANK.at_risk ? "text-destructive" : latest <= RANK.watch ? "text-brass" : "text-muted-foreground";

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={`${tone} opacity-70 shrink-0`}
      aria-hidden="true"
      data-testid="health-sparkline"
    >
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      {points.length === 1 && (
        <circle cx={width / 2} cy={height - 2 - (points[0].rank / rMax) * (height - 3)} r="1.5" fill="currentColor" />
      )}
    </svg>
  );
}
